// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, withLatestFrom } from 'rxjs/operators';

// EXTERNAL DEPENDENCY
import { get } from 'lodash';

// SERVICES
import { EmployeeService } from '../services/employee.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as AuthActions from '../../auth/store/actions';
import * as EmployeeActions from './actions';


// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { EMPLOYEE_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/employee';


/**
 * This effect is for listen Employee actions, execute Employee service call and dispatch in Employee reducer.
 * For Employee api call purpose.
 *
 * @author Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 30/03/2020 (Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>)  created Employee effect
 */
@Injectable()
export class EmployeeEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private employeeService: EmployeeService) { }

  /**
   * @param type : Employee action name.
      * Employee Effect
      * @param payload : object to identify common Employee method base on module and method.
      * @returns Empty action dispatch to reducer.
      */
  EmployeeCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<EmployeeActions.EmployeeCommonAction>(EmployeeActions.ActionTypes.EMPLOYEE_COMMON),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = EMPLOYEE_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      if (state[common.STATE]) {
        return of(new globalActions.EmptyAction({}));
      }
      // Only sending payload data
      return this.employeeService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {

            let actions: any[] = [];


            // dispatch Employee success action
            actions = [new EmployeeActions.EmployeeCommonSuccessAction({ data, common })];
            if (common.API_METHOD === 'updatePersonalDetails') {
              actions.push(new AuthActions.UserProfileSuccessAction({ data, common }));
            }
            if (common.SUCCESS_NOTIFICATION) {
              if (data.isError) {
                action.payload.callback({ success: true, response: common.API_RESPONSE ? get(data, common.API_RESPONSE) : data });
              } else {
                // dispatch popup notification for open success popup
                actions.push(new globalActions.PopupNotificationAction({
                  actionType: EmployeeActions.ActionTypes.EMPLOYEE_COMMON, type: ModalType.SUCCESS,
                  callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                  code: `${common.SUCCESS}`, response: common.API_RESPONSE ? get(data, common.API_RESPONSE) : data,
                }));
              }
            } else {
              if (action.payload.callback) {
                if (common.API_RESPONSE) {
                  action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
                } else {
                  action.payload.callback({ success: true, data });

                }
              }
            }

            return actions;
          }),
          catchError(error => {
            return of(new globalActions.PopupNotificationAction({
              actionType: EmployeeActions.ActionTypes.EMPLOYEE_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          }));
    }),
  ));


}
